import { RelatedPartyRoleDto, RelatedPartyRoleUpdateDto } from 'types/compliance';

/** How a client identification tier asks a role: one choice per tier cell. */
export type TierChoice = 'NOT_ASKED' | 'ASKED' | 'MANDATORY';

/** Only a list role can be mandatory; the entity takes no tier at all. */
export const tierChoicesOf = (role: RelatedPartyRoleDto): TierChoice[] =>
    role.kind === 'LIST' ? ['NOT_ASKED', 'ASKED', 'MANDATORY'] : ['NOT_ASKED', 'ASKED'];

/**
 * A retired tier can only be dropped: the backend refuses it on a role that does not hold it already. The
 * choices come from the saved role, so a tier dropped in this edit can still be taken back.
 */
export const retiredTierChoicesOf = (savedRole: RelatedPartyRoleDto, tierCode: string): TierChoice[] => {
    const savedChoice = tierChoiceOf(savedRole, tierCode);
    return savedChoice === 'NOT_ASKED' ? [] : ['NOT_ASKED', savedChoice];
};

/** The tiers the saved roles still hold that the dictionary no longer offers, in the order they are met. */
export const retiredTierCodesHeldBy = (roles: RelatedPartyRoleDto[], liveTierCodes: string[]): string[] => {
    const retired = new Set<string>();
    roles.forEach((role) =>
        role.identificationTierCodes?.forEach((code) => {
            if (!liveTierCodes.includes(code)) retired.add(code);
        })
    );
    return [...retired];
};

export const tierChoiceOf = (role: RelatedPartyRoleDto, tierCode: string): TierChoice => {
    if (!role.identificationTierCodes?.includes(tierCode)) return 'NOT_ASKED';
    return role.identificationRequiredTierCodes?.includes(tierCode) ? 'MANDATORY' : 'ASKED';
};

const inOrder = (codes: Set<string>, order: string[]): string[] => [
    ...order.filter((code) => codes.has(code)),
    ...[...codes].filter((code) => !order.includes(code)),
];

/** Not asked drops the tier from the mandatory ones too, so a mandatory tier is always one that asks the role. */
export const withTierChoice = (
    role: RelatedPartyRoleDto,
    tierCode: string,
    choice: TierChoice,
    tierOrder: string[]
): RelatedPartyRoleDto => {
    const tiers = new Set(role.identificationTierCodes ?? []);
    const required = new Set(role.identificationRequiredTierCodes ?? []);
    if (choice === 'NOT_ASKED') tiers.delete(tierCode);
    else tiers.add(tierCode);
    if (choice === 'MANDATORY') required.add(tierCode);
    else required.delete(tierCode);
    return {
        ...role,
        identificationTierCodes: inOrder(tiers, tierOrder),
        identificationRequiredTierCodes:
            role.identificationRequiredTierCodes === null ? null : inOrder(required, tierOrder),
    };
};

export const withItemKey = (
    role: RelatedPartyRoleDto,
    itemKey: string,
    isChecked: boolean,
    itemOrder: string[]
): RelatedPartyRoleDto => {
    const keys = new Set(role.identificationItemKeys ?? []);
    if (isChecked) keys.add(itemKey);
    else keys.delete(itemKey);
    return { ...role, identificationItemKeys: inOrder(keys, itemOrder) };
};

export const isAskedInAnyTier = (role: RelatedPartyRoleDto): boolean => (role.identificationTierCodes ?? []).length > 0;

/** A role a tier asks must ask at least one detail; the backend refuses the save otherwise. */
export const lacksDetails = (role: RelatedPartyRoleDto): boolean =>
    isAskedInAnyTier(role) && (role.identificationItemKeys ?? []).length === 0;

export const toUpdate = (role: RelatedPartyRoleDto): RelatedPartyRoleUpdateDto => ({
    code: role.code,
    isEnabled: role.isEnabled,
    identificationTierCodes: role.identificationTierCodes,
    identificationRequiredTierCodes: role.identificationRequiredTierCodes,
    identificationItemKeys: role.identificationItemKeys,
});
